import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgencyAnalytics } from '@/hooks/useAgencyAnalytics';
import { useRentalAgencyAnalytics } from '@/hooks/useRentalAgencyAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import AppSidebar from '@/components/AppSidebar';
import { ErrorDisplay } from '@/components/dashboard';
import AgencyAnalyticsHeader from '@/components/dashboard/AgencyAnalyticsHeader';
import AgencyListingsTable from '@/components/dashboard/AgencyListingsTable';
import AgencyDistributionCharts from '@/components/dashboard/AgencyDistributionCharts';
import { useTableSorting } from '@/hooks/useTableSorting';

const AgencyAnalytics = () => {
  const [selectedTab, setSelectedTab] = useState<'venda' | 'aluguel'>('venda');
  const [selectedAgency, setSelectedAgency] = useState<string | null>(null);
  const chartsRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { state } = useSidebar();
  const navigate = useNavigate();

  const { 
    data: salesData, 
    isLoading: isSalesLoading, 
    error: salesError,
    refetch: refetchSales 
  } = useAgencyAnalytics();

  const { 
    data: rentalData, 
    isLoading: isRentalLoading, 
    error: rentalError,
    refetch: refetchRental 
  } = useRentalAgencyAnalytics();

  const currentData = selectedTab === 'venda' ? salesData : rentalData;
  const isLoading = selectedTab === 'venda' ? isSalesLoading : isRentalLoading;
  const error = selectedTab === 'venda' ? salesError : rentalError;
  const refetch = selectedTab === 'venda' ? refetchSales : refetchRental;

  const { 
    currentPage,
    setCurrentPage,
    sortField,
    sortDirection,
    handleSort,
    paginatedData,
    totalPages,
  } = useTableSorting(currentData?.resumo_geral || []);

  if (error) {
    return (
      <div className="p-6">
        <ErrorDisplay 
          error={error instanceof Error ? error.message : String(error)} 
          onRetry={refetch} 
        />
      </div>
    );
  }

  const resumoGeral = currentData?.resumo_geral || [];
  const distribuicaoPorTipo = currentData?.distribuicao_por_tipo || [];
  const distribuicaoPorBairro = currentData?.distribuicao_por_bairro || [];

  const totalAgencies = resumoGeral.length;
  const totalListings = resumoGeral.reduce((acc, curr) => acc + curr.total_anuncios, 0);
  const totalValue = resumoGeral.reduce((acc, curr) => acc + curr.valor_total, 0);

  const selectedAgencyTypeDistribution = selectedAgency
    ? distribuicaoPorTipo
        .filter(item => item.anunciante === selectedAgency)
        .map(item => ({
          name: item.tipo,
          value: item.quantidade,
          percentual: item.percentual
        }))
    : [];

  const selectedAgencyNeighborhoodDistribution = selectedAgency
    ? distribuicaoPorBairro
        .filter(item => item.anunciante === selectedAgency)
        .map(item => ({
          name: item.bairro,
          value: item.quantidade,
          percentual: item.percentual
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)
    : [];

  const handleAgencySelect = (agency: string) => {
    setSelectedAgency(agency);
    setTimeout(() => {
      chartsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="flex min-h-screen w-full bg-white">
      <AppSidebar activePage="agency-analytics" />
      
      <div className={`flex-1 overflow-auto p-4 md:p-6 ${state === "collapsed" ? "ml-10" : ""} ${isMobile ? "mt-14" : ""}`}>
        <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as 'venda' | 'aluguel')}>
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold">Análise de Imobiliárias</h1>
              <p className="text-muted-foreground">
                Posição das {totalAgencies} maiores imobiliárias no mercado de {selectedTab} na última semana. Fonte: Anúncios filtrados do Infoimóveis.
              </p>
              <p className="text-muted-foreground" text-sm>
                O tempo médio dos anúncios é calculado a partir da data da última atualização realizada no anúncio.
              </p>
            </div>
            <TabsList className="self-start md:self-center">
              <TabsTrigger value="venda">Venda</TabsTrigger>
              <TabsTrigger value="aluguel">Aluguel</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="venda" className="space-y-6">
            <AgencyAnalyticsHeader
              totalAgencies={totalAgencies}
              totalListings={totalListings}
              totalValue={totalValue}
              type="venda"
            />

            <div className="mt-6">
              <AgencyListingsTable
                data={paginatedData}
                onAgencySelect={handleAgencySelect}
                type="venda"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </div>
          </TabsContent>

          <TabsContent value="aluguel" className="space-y-6">
            <AgencyAnalyticsHeader
              totalAgencies={totalAgencies}
              totalListings={totalListings}
              totalValue={totalValue}
              type="aluguel"
            />

            <div className="mt-6">
              <AgencyListingsTable
                data={paginatedData}
                onAgencySelect={handleAgencySelect}
                type="aluguel"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </div>
          </TabsContent>
        </Tabs>

        {selectedAgency && (
          <div className="mt-6" ref={chartsRef}>
            <h2 className="text-xl font-bold mb-4">
              Distribuição de Imóveis - {selectedAgency}
            </h2>
            <AgencyDistributionCharts
              typeDistribution={selectedAgencyTypeDistribution}
              neighborhoodDistribution={selectedAgencyNeighborhoodDistribution}
              type={selectedTab}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AgencyAnalytics;
